/**
 * Headless twin runtime: follows the shell selection (assetId), loads the
 * element JSON and the asset spec into the twin store, and keeps the derived
 * subsystem statuses in step with the readings. Mounted once by the Shell.
 */
import { useEffect } from 'react';
import { useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { deriveAllStatuses, deriveComponentStatus, withReadings } from './analysis';
import { registerDeriveStatusHook } from './sim/deriveStatusHook';
import { loadAssetSpec, loadElement } from './data';
import { findSubsystem, useTwin } from './store';

const STATUS_DEBOUNCE_MS = 150;

export function TwinRuntime() {
  const assetId = useUI((s) => s.selection.assetId);
  const element = useTwin((s) => s.element);
  const readings = useTwin((s) => s.readings);

  // Load the element and its asset spec whenever the selected asset changes.
  useEffect(() => {
    const twin = useTwin.getState();
    if (twin.assetId === assetId && (twin.loadStatus === 'ready' || twin.loadStatus === 'loading')) return;
    twin.setAsset(assetId);
    if (!assetId) return;
    let cancelled = false;
    (async () => {
      const spec = await loadAssetSpec(assetId);
      const el = await loadElement(spec.elementId);
      if (cancelled) return;
      useTwin.getState().setLoaded(el, spec);
    })().catch((err: unknown) => {
      if (cancelled) return;
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[twin] failed to load asset ${assetId}:`, err);
      useTwin.getState().setLoadError(message);
      toast('error', `Could not load the twin for ${assetId}: ${message}`);
    });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  // The simulator asks for a component's derived level on every batch (alerts on DEGRADED and OFFLINE).
  useEffect(() => {
    registerDeriveStatusHook((subsystemId, readings) => {
      const twin = useTwin.getState();
      if (!twin.element) return 'OPERATIONAL';
      const merged = withReadings(twin.element.subsystems, readings, twin.history);
      const sub = findSubsystem(merged, subsystemId);
      return sub ? deriveComponentStatus(sub).status : 'OPERATIONAL';
    });
    return () => registerDeriveStatusHook(null);
  }, []);

  // Derived statuses follow the readings (debounced: simulation batches arrive together).
  useEffect(() => {
    if (!element) return;
    const timer = window.setTimeout(() => {
      try {
        const twin = useTwin.getState();
        twin.setStatuses(deriveAllStatuses(element, twin.readings, twin.history));
      } catch (err) {
        console.error('[twin] status derivation failed:', err);
      }
    }, STATUS_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [element, readings]);

  return null;
}
